import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

// Components
import CustomTable from '../../components/CustomTable';
import SearchBar from '../../components/search.component';
import SecretPanel from './SecretPanel';
import DeleteConfirmDialog from '../../components/deleteConfirmation.component';
import SecretTypeSelect from '../../components/secretTypeSelect.component';
import AutocompleteServerList from '../../components/serverAutocomplete.component';

// Services and Utils
import apiRequest from '../../services/apiClient';
import { formatDate, secretTypeDisplayTextTranslated } from '../../utils/utils';
import { secretTypes } from '../../utils/enums';
import { invalidateCache } from '../../components/serverAutocomplete.component';

const Secrets = ({ setTitleProps }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [secrets, setSecrets] = useState({ items: [], totalCount: 0 });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [sortModel, setSortModel] = useState([{ field: 'name', sort: 'asc' }]);
  const [filters, setFilters] = useState({
    searchQuery: '',
    typeFilter: '',
    sectionFilter: '',
    clientFilter: ''
  });
  const [selectedItem, setSelectedItem] = useState();
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState();
  const isExecutingRef = useRef(false);

  const loadSecrets = () => {
    if (isExecutingRef.current) return;
    isExecutingRef.current = true;

    try {
      setLoading(true);
      const order = sortModel && sortModel.length > 0
        ? `&$orderby=${sortModel[0].field} ${sortModel[0].sort}`
        : "";

      let searchQueryFilter = filters?.searchQuery && filters.searchQuery.length > 0 && filters.searchQuery.trim().length > 0
        ? `contains(name,'${filters.searchQuery}') or contains(notes,'${filters.searchQuery}')`
        : "";

      let filter = '';

    if (filters.typeFilter && filters.typeFilter.length > 0) {
      const secretType = secretTypes.find(st => st.value === parseInt(filters.typeFilter));
      if (secretType) {
        filter += `type eq ${secretType.enumFilter}`;
      }
    }

      if (filters.sectionFilter && filters.sectionFilter.length > 0) {
        if (filter && filter.length > 0) filter += " and ";
        filter += `sectionId eq ${filters.sectionFilter}`;
      } else {
        if (searchQueryFilter.length > 0) {
          searchQueryFilter += ` or contains(section/name,'${filters.searchQuery}')`;
        }
      }

      if (filters.clientFilter && filters.clientFilter.length > 0) {
        if (filter && filter.length > 0) filter += " and ";
        filter += `clientId eq ${filters.clientFilter}`;
      } else {
        if (searchQueryFilter.length > 0) {
          searchQueryFilter += ` or contains(client/name,'${filters.searchQuery}')`;
        }
      }

      if (searchQueryFilter.length > 0) {
        if (filter.length > 0) {
          filter = `(${filter}) and (${searchQueryFilter})`;
        } else {
          filter = searchQueryFilter;
        }
      }
      
    if (filter && filter.length > 0)
      filter = "&$filter=" + filter;

    const finalUrl = `secretItem/paged?$select=id,created,type,name&$expand=section($select=name,backgroundColor,fontColor),client($select=name)&$top=${pageSize}&$skip=${page*pageSize}${order}${filter}`;

      apiRequest(finalUrl)
        .then((response) => {
          setSecrets({
            items: response.data.items || response.data,
            totalCount: response.data.totalCount || response.totalCount || 0
          });
          setLoading(false);
          isExecutingRef.current = false;
        })
        .catch((error) => {
          console.error('Error loading secrets:', error);
          setLoading(false);
          isExecutingRef.current = false;
        });
    } catch (error) {
      console.error('Error in loadSecrets:', error);
      setLoading(false);
      isExecutingRef.current = false;
    }
  };

  useEffect(() => {
    setTitleProps("Secrets");
  }, []);

  useEffect(() => {
    loadSecrets();
  }, [page, pageSize, sortModel, filters.searchQuery, filters.typeFilter, filters.sectionFilter, filters.clientFilter]);

  const onHandlePageSizeChange = (newSize) => {
    setPageSize(newSize);
  };

  const handleSortModelChange = (newModel) => {
    setSortModel(newModel);
  };

  const handleOnSearchQueryFilterChange = (searchQuery) => {
    setFilters({
      ...filters,
      searchQuery
    });
  };

  const handleOnTypeFilterChange = (typeFilter) => {
    setFilters({
      ...filters,
      typeFilter
    });
  };

  const handleOnSectionFilterChange = (sectionFilter) => {
    setFilters({
      ...filters,
      sectionFilter
    });
  };

  const handleOnClientFilterChange = (clientFilter) => {
    setFilters({
      ...filters,
      clientFilter
    });
  };

  const updateFilter = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleRowSelection = useCallback((e) => {       
    setSelectedItem(e[0]);
  }, []);

  const handleUnselectItem = useCallback(() => {
    setSelectedItem(null);
  }, []);

  const handleDeleteConfirmation = useCallback((itemId) => {
    setDeleteItemId(itemId);
    setConfirmOpen(true);
  }, []);

  const handleSecretAdd = useCallback(() => {
    enqueueSnackbar('Item successfully added!', {
      variant: 'success',
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
      autoHideDuration: 3000
    });
    setShowForm(false);
    loadSecrets();
  }, [enqueueSnackbar]);

  const handleEditItem = useCallback(() => {
    setShowForm(true);
  }, []);

  const handleDeleteItem = useCallback((itemId) => {
    handleDeleteConfirmation(itemId);
  }, [handleDeleteConfirmation]);

  const handleDelete = (itemId) => {
    setLoading(true);
    apiRequest(`secretItem/${itemId}`, {
      method: "DELETE"
    })
    .then(() => {
      enqueueSnackbar('Item successfully deleted!', {
        variant: 'success',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        autoHideDuration: 3000
      });
      loadSecrets();
      handleUnselectItem(); // Cerrar el panel de detalles después de eliminar
      // Invalidar caché de secrets para refrescar las listas
      invalidateCache('secretItem');
    })
    .catch((error) => {
      setLoading(false);
      enqueueSnackbar(error.response?.data?.message ?? "An unknown error occurred", {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
        autoHideDuration: 3000
      });
    });
  };

  const columns = useMemo(() => [
    { 
      field: 'name', 
      headerName: 'Name', 
      minWidth: 300,
      sortable: true
    },
    { 
      field: 'type',
      headerName: 'Type',
      minWidth: 200,
      valueGetter: (params) => {
        return secretTypeDisplayTextTranslated(params.value, t);
      },
      sortable: true
    },
    { 
      field: 'section', 
      headerName: 'Section',
      minWidth: 150,
      valueGetter: (params) => params.row.section?.name || 'N/A',
      sortable: false
    },
    { 
      field: 'client', 
      headerName: 'Client',
      minWidth: 150,
      valueGetter: (params) => params.row.client?.name || 'N/A',
      sortable: false
    },
    {
      field: 'created',
      headerName: 'Created',
      minWidth: 150,
      type: 'dateTime',
      valueGetter: (params) => {
        return formatDate(params.value);
      },
      sortable: true
    }
  ], [t]);

  const memoizedSecretPanel = useMemo(() => (
    <SecretPanel
      selectedItem={selectedItem}
      showForm={showForm}
      handleUnselectItem={handleUnselectItem}
      handleEditItem={handleEditItem}
      handleDeleteItem={handleDeleteItem}
      handleDeleteConfirmation={handleDeleteConfirmation}
      handleSecretAdd={handleSecretAdd}
      setShowForm={setShowForm}
    />
  ), [selectedItem, showForm, handleUnselectItem, handleEditItem, handleDeleteItem, handleDeleteConfirmation, handleSecretAdd]);

  return (
    <div className="p-6">
      {/* Header with Add button and Search */}
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (selectedItem) {
                  setSelectedItem(null);
                }
                setShowForm(true);
              }}
              className="btn-primary flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add
            </button>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
              </svg>
              Filters
            </button>
          </div>
          
          <div className="max-w-md">
            <SearchBar onTextChange={handleOnSearchQueryFilterChange} disabled={loading} />
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <SecretTypeSelect
                value={filters.typeFilter}
                onChange={(value) => updateFilter('typeFilter', value)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Section</label>
              <AutocompleteServerList
                entityName="section"
                selectQuery="?$select=id,name"
                valueProperty="id"
                displayProperty="name"
                value={filters.sectionFilter}
                onChange={(value) => updateFilter('sectionFilter', value)}
                placeholder="Select section..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
              <AutocompleteServerList
                entityName="client"
                selectQuery="?$select=id,name"
                valueProperty="id"
                displayProperty="name"
                value={filters.clientFilter}
                onChange={(value) => updateFilter('clientFilter', value)}
                placeholder="Select client..."
              />
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <CustomTable
        rows={secrets.items}
        columns={columns}
        loading={loading}
        pageSize={pageSize}
        page={page}
        totalCount={secrets.totalCount}
        onPageChange={setPage}
        onPageSizeChange={onHandlePageSizeChange}
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
        onRowClick={handleRowSelection}
        selectedRow={selectedItem}
        onView={(row) => setSelectedItem(row.id)}
        onEdit={(row) => {
          setSelectedItem(row.id);
          setShowForm(true);
        }}
        onDelete={(row) => handleDeleteConfirmation(row.id)}
      />

      {/* Detail Panel */}
      {(selectedItem || showForm) && (
        <div className="fixed inset-0 z-50 sm:inset-auto sm:right-0 sm:top-0 sm:h-full sm:w-96">
          {/* Desktop: Drawer lateral derecho */}
          <div className="hidden sm:block fixed right-0 top-0 h-full w-96 bg-white shadow-xl">
            <div className="flex flex-col h-full">
              <div className="p-6 pb-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {showForm ? (selectedItem ? 'Edit Secret' : 'Add Secret') : 'Secret Details'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      handleUnselectItem();
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    title="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                {memoizedSecretPanel}
              </div>
            </div>
          </div>
          
          {/* Mobile: Modal full screen */}
          <div className="sm:hidden fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white shadow-xl w-full h-full flex flex-col">
              <div className="p-6 pb-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {showForm ? (selectedItem ? 'Edit Secret' : 'Add Secret') : 'Secret Details'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowForm(false);
                      handleUnselectItem();
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                    title="Close"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-hidden">
                {memoizedSecretPanel}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog 
        title="Delete Secret?"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => handleDelete(deleteItemId)}
      >
        Are you sure you want to delete this secret?
      </DeleteConfirmDialog>
    </div>
  );
};

export default Secrets;